import { Controller } from "@hotwired/stimulus";

const CONDITIONS_WITH_OPERATOR = new Set(["account_age_days", "creation_date", "username_pattern"]);
const CONDITIONS_WITH_VALUE = new Set([
  "account_age_days",
  "creation_date",
  "username_pattern",
  "username_blocked_words",
]);
const ACTIONS_WITH_ROLE = new Set(["add_role"]);
const ACTIONS_WITH_MESSAGE = new Set(["send_dm", "notify_staff"]);
const ACTIONS_WITH_CHANNEL = new Set(["notify_staff"]);

const OPERATORS_FOR = {
  account_age_days: ["less_than", "greater_than"],
  creation_date: ["on", "before", "after"],
  username_pattern: ["matches"],
};

function labelFor(map, key) {
  return map[key] || key;
}

// Labels come from the *Labels Stimulus values (server-rendered i18n text)
// and are spliced into an innerHTML template below, so they must be escaped.
function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default class ScreeningFiltersController extends Controller {
  static targets = ["filterList", "filter", "json"];
  static values = {
    filterLabelPlaceholder: String,
    removeFilterAriaLabel: String,
    conditionsHeading: String,
    addConditionLabel: String,
    actionsHeading: String,
    addActionLabel: String,
    removeAriaLabel: String,
    roleIdPlaceholder: String,
    channelIdPlaceholder: String,
    messagePlaceholder: String,
    conditionValuePlaceholders: Object,
    conditionLabels: Object,
    operatorLabels: Object,
    actionLabels: Object,
  };

  // ── Serialization ────────────────────────────────────────────────────────────

  serializeFilters(_event) {
    const filters = this.filterTargets
      .map((filter) => {
        const label =
          filter.querySelector("[data-screening-filters-target='filterLabel']")?.value || "";

        const conditions = [
          ...filter.querySelectorAll("[data-screening-filters-target='condition']"),
        ]
          .map((row) => {
            const type =
              row.querySelector("[data-screening-filters-target='conditionType']")?.value || "";
            const operator =
              row.querySelector("[data-screening-filters-target='conditionOperator']")?.value || "";
            const value =
              row.querySelector("[data-screening-filters-target='conditionValue']")?.value || "";
            const cond = { type };
            if (CONDITIONS_WITH_OPERATOR.has(type)) cond.operator = operator;
            if (CONDITIONS_WITH_VALUE.has(type)) cond.value = value;
            return cond;
          })
          .filter((c) => c.type);

        const actions = [...filter.querySelectorAll("[data-screening-filters-target='action']")]
          .map((row) => this.#buildAction(row))
          .filter((a) => a.type);

        return { label, conditions, actions };
      })
      .filter((f) => f.conditions.length > 0 && f.actions.length > 0);

    if (this.hasJsonTarget) {
      this.jsonTarget.value = JSON.stringify(filters);
    }
  }

  // ── Filter card ──────────────────────────────────────────────────────────────

  addFilter() {
    const idx = this.filterTargets.length;
    const card = document.createElement("div");
    card.className = "border border-base-300 rounded-lg p-4 mb-4";
    card.dataset.screeningFiltersTarget = "filter";
    card.innerHTML = this.#filterCardHTML(idx);
    this.filterListTarget.appendChild(card);
  }

  removeFilter(event) {
    event.currentTarget.closest("[data-screening-filters-target='filter']")?.remove();
  }

  // ── Condition row ────────────────────────────────────────────────────────────

  addCondition(event) {
    const filter = event.currentTarget.closest("[data-screening-filters-target='filter']");
    const list = filter.querySelector("[data-screening-filters-target='conditionList']");
    const row = document.createElement("div");
    row.className = "flex items-center gap-2 mb-2";
    row.dataset.screeningFiltersTarget = "condition";
    row.innerHTML = this.#conditionRowHTML("account_age_days");
    list.appendChild(row);
  }

  removeCondition(event) {
    event.currentTarget.closest("[data-screening-filters-target='condition']")?.remove();
  }

  conditionTypeChanged(event) {
    const select = event.currentTarget;
    const type = select.value;
    const row = select.closest("[data-screening-filters-target='condition']");

    const opSel = row.querySelector("[data-screening-filters-target='conditionOperator']");
    const valIn = row.querySelector("[data-screening-filters-target='conditionValue']");

    const hasOp = CONDITIONS_WITH_OPERATOR.has(type);
    const hasVal = CONDITIONS_WITH_VALUE.has(type);

    opSel.classList.toggle("hidden", !hasOp);
    valIn.classList.toggle("hidden", !hasVal);
    valIn.placeholder = this.conditionValuePlaceholdersValue[type] || "";

    // Repopulate operator options
    opSel.innerHTML = "";
    (OPERATORS_FOR[type] || []).forEach((op) => {
      const opt = document.createElement("option");
      opt.value = op;
      opt.text = labelFor(this.operatorLabelsValue, op);
      opSel.appendChild(opt);
    });
  }

  // ── Action row ───────────────────────────────────────────────────────────────

  addAction(event) {
    const filter = event.currentTarget.closest("[data-screening-filters-target='filter']");
    const list = filter.querySelector("[data-screening-filters-target='actionList']");
    const row = document.createElement("div");
    row.className = "flex items-center gap-2 mb-2";
    row.dataset.screeningFiltersTarget = "action";
    row.innerHTML = this.#actionRowHTML("add_role");
    list.appendChild(row);
  }

  removeAction(event) {
    event.currentTarget.closest("[data-screening-filters-target='action']")?.remove();
  }

  actionTypeChanged(event) {
    const select = event.currentTarget;
    const type = select.value;
    const row = select.closest("[data-screening-filters-target='action']");

    row
      .querySelector("[data-screening-filters-target='actionRoleId']")
      ?.classList.toggle("hidden", !ACTIONS_WITH_ROLE.has(type));
    row
      .querySelector("[data-screening-filters-target='actionChannelId']")
      ?.classList.toggle("hidden", !ACTIONS_WITH_CHANNEL.has(type));
    row
      .querySelector("[data-screening-filters-target='actionMessage']")
      ?.classList.toggle("hidden", !ACTIONS_WITH_MESSAGE.has(type));
  }

  // ── HTML templates ───────────────────────────────────────────────────────────

  #buildAction(row) {
    const g = (t) => {
      const el = row.querySelector(`[data-screening-filters-target='${t}']`);
      return el ? el.value || "" : "";
    };
    const type = g("actionType");
    const act = { type };
    if (ACTIONS_WITH_ROLE.has(type)) act.role_id = g("actionRoleId");
    if (ACTIONS_WITH_CHANNEL.has(type)) act.channel_id = g("actionChannelId");
    if (ACTIONS_WITH_MESSAGE.has(type)) act.message = g("actionMessage");
    return act;
  }

  #filterCardHTML(_idx) {
    const firstCond = this.#conditionRowHTML("account_age_days");
    const firstAct = this.#actionRowHTML("add_role");
    return `
      <div class="flex items-center justify-between mb-3">
        <input class="input input-bordered input-sm w-48" type="text"
               placeholder="${this.filterLabelPlaceholderValue || "Filter name (optional)"}"
               data-screening-filters-target="filterLabel">
        <button type="button" class="btn btn-ghost btn-sm btn-square text-error/70 hover:text-error"
                aria-label="${this.removeFilterAriaLabelValue || "Remove filter"}"
                data-action="click->screening-filters#removeFilter">
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>
      <p class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-2">${this.conditionsHeadingValue || "Conditions"}</p>
      <div data-screening-filters-target="conditionList">
        <div class="flex items-center gap-2 mb-2" data-screening-filters-target="condition">
          ${firstCond}
        </div>
      </div>
      <button type="button" class="btn btn-xs btn-outline btn-neutral mb-4"
              data-action="click->screening-filters#addCondition">
        <i class="fa-solid fa-plus mr-1"></i> ${this.addConditionLabelValue || "Add Condition"}
      </button>
      <p class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-2">${this.actionsHeadingValue || "Actions"}</p>
      <div data-screening-filters-target="actionList">
        <div class="flex items-center gap-2 mb-2" data-screening-filters-target="action">
          ${firstAct}
        </div>
      </div>
      <button type="button" class="btn btn-xs btn-outline btn-neutral"
              data-action="click->screening-filters#addAction">
        <i class="fa-solid fa-plus mr-1"></i> ${this.addActionLabelValue || "Add Action"}
      </button>
    `;
  }

  #conditionTypeOptions(selected) {
    return (
      Object.entries(this.conditionLabelsValue)
        .map(
          ([v, t]) =>
            `<option value="${escapeHtml(v)}"${v === selected ? " selected" : ""}>${escapeHtml(t)}</option>`,
        )
        .join("") || `<option value="${escapeHtml(selected)}">${escapeHtml(selected)}</option>`
    );
  }

  #actionTypeOptions(selected) {
    return (
      Object.entries(this.actionLabelsValue)
        .map(
          ([v, t]) =>
            `<option value="${escapeHtml(v)}"${v === selected ? " selected" : ""}>${escapeHtml(t)}</option>`,
        )
        .join("") || `<option value="${escapeHtml(selected)}">${escapeHtml(selected)}</option>`
    );
  }

  #conditionRowHTML(type) {
    const hasOp = CONDITIONS_WITH_OPERATOR.has(type);
    const hasVal = CONDITIONS_WITH_VALUE.has(type);
    const ops = (OPERATORS_FOR[type] || [])
      .map(
        (op) =>
          `<option value="${escapeHtml(op)}">${escapeHtml(labelFor(this.operatorLabelsValue, op))}</option>`,
      )
      .join("");
    const placeholder = this.conditionValuePlaceholdersValue[type] || "";
    return `
      <select class="select select-bordered select-xs w-44"
              data-action="change->screening-filters#conditionTypeChanged"
              data-screening-filters-target="conditionType">
        ${this.#conditionTypeOptions(type)}
      </select>
      <select class="select select-bordered select-xs w-28${hasOp ? "" : " hidden"}"
              data-screening-filters-target="conditionOperator">
        ${ops}
      </select>
      <input class="input input-bordered input-xs flex-1${hasVal ? "" : " hidden"}"
             type="text" placeholder="${escapeHtml(placeholder)}"
             data-screening-filters-target="conditionValue">
      <button type="button" class="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error shrink-0"
              aria-label="${this.removeAriaLabelValue || "Remove condition"}"
              data-action="click->screening-filters#removeCondition">
        <i class="fa-solid fa-trash-can"></i>
      </button>
    `;
  }

  #actionRowHTML(type) {
    return `
      <select class="select select-bordered select-xs w-40"
              data-action="change->screening-filters#actionTypeChanged"
              data-screening-filters-target="actionType">
        ${this.#actionTypeOptions(type)}
      </select>
      <input class="input input-bordered input-xs w-36${ACTIONS_WITH_ROLE.has(type) ? "" : " hidden"}"
             type="text" placeholder="${this.roleIdPlaceholderValue || "Role ID"}"
             data-screening-filters-target="actionRoleId">
      <input class="input input-bordered input-xs w-28${ACTIONS_WITH_CHANNEL.has(type) ? "" : " hidden"}"
             type="text" placeholder="${this.channelIdPlaceholderValue || "Channel ID"}"
             data-screening-filters-target="actionChannelId">
      <input class="input input-bordered input-xs flex-1${ACTIONS_WITH_MESSAGE.has(type) ? "" : " hidden"}"
             type="text" placeholder="${this.messagePlaceholderValue || "Message text"}"
             data-screening-filters-target="actionMessage">
      <button type="button" class="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error shrink-0"
              aria-label="${this.removeAriaLabelValue || "Remove action"}"
              data-action="click->screening-filters#removeAction">
        <i class="fa-solid fa-trash-can"></i>
      </button>
    `;
  }
}
